import { isEqual } from 'lodash-es'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { PlacementId } from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { EmbroideryTypeKey } from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import { PreviewMark } from '../../primitives/preview/previewMarks'

/** The open editor's unsaved work, which the preview draws before anything is saved. */
export type PreviewDraft = {
  typeKey: EmbroideryTypeKey
  /**
   * Replaces the saved items of the embroidery type being edited, so it must include the ones
   * still saved.
   */
  marks: PreviewMark[]
  /** The placement the preview shows close up; undefined shows the whole garment. */
  focusedPlacementId: PlacementId | undefined
}

type SetPreviewDraft = (draft: PreviewDraft | undefined) => void

const PreviewDraftContext = createContext<PreviewDraft | undefined>(undefined)

// Kept apart from the draft so editors don't re-render when their own update lands.
const SetPreviewDraftContext = createContext<SetPreviewDraft>(() => {})

/**
 * Lets the open editor show its unsaved work in the preview, which otherwise only sees saved items.
 *
 * Sending the draft up through an effect is not ideal. The cleaner fix is to move each editor's
 * state into the flow that mounts it, but then every flow would have to rebuild the preview
 * wiring for itself. With this provider a flow just wraps its shell and the preview works.
 */
export const PreviewDraftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [draft, setDraftState] = useState<PreviewDraft>()

  // Editors rebuild their draft every render, so keep the current one when nothing in it changed.
  const setDraft = useCallback<SetPreviewDraft>(
    next => setDraftState(current => (isEqual(current, next) ? current : next)),
    []
  )

  return (
    <SetPreviewDraftContext.Provider value={setDraft}>
      <PreviewDraftContext.Provider value={draft}>{children}</PreviewDraftContext.Provider>
    </SetPreviewDraftContext.Provider>
  )
}

/** The open editor's draft, or undefined while no editor is open. */
export const usePreviewDraft = () => useContext(PreviewDraftContext)

/**
 * Puts this draft in the preview and keeps it current while the calling editor is open, then
 * takes it down when the editor closes. Only one editor may call it at a time. Does nothing
 * outside a `PreviewDraftProvider`.
 */
export function useShowDraftInPreview(draft: PreviewDraft): void {
  const setDraft = useContext(SetPreviewDraftContext)

  // Sends the draft after every render. The provider drops it when nothing in it changed.
  useEffect(() => {
    setDraft(draft)
  }, [setDraft, draft])

  // Runs once, so its cleanup fires only when the editor closes. As the cleanup of the effect
  // above, it would clear the draft before every update instead.
  useEffect(() => () => setDraft(undefined), [setDraft])
}
